import { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, 
  PieChart, Pie, Cell, LineChart, Line, CartesianGrid, Legend
} from 'recharts';
import { TrendingUp, DollarSign, Users, Target } from 'lucide-react';
import CRMLayout from '../../components/CRM/CRMLayout';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const COLORS = ['#8b5cf6', '#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#06b6d4', '#ec4899', '#6366f1', '#84cc16'];

const STAGE_LABELS = {
  lead: 'Lead', contacted: 'Contacted', application: 'Application',
  submitted: 'Submitted', approved: 'Approved', declined: 'Declined',
  docs_out: 'Docs Out', settled: 'Settled', lost: 'Lost'
};

export default function Analytics() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAnalytics();
  }, []);

  const fetchAnalytics = async () => {
    try {
      const response = await axios.get(`${API}/crm/analytics`);
      setData(response.data);
    } catch (error) {
      console.error('Error fetching analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <CRMLayout>
        <div className="flex items-center justify-center h-screen">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-500"></div>
        </div>
      </CRMLayout>
    );
  }

  if (!data) return null;

  // Transform data for charts
  const monthlyData = Object.entries(data.monthly_deals)
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([month, count]) => ({
      month: new Date(month + '-01').toLocaleDateString('en-AU', { month: 'short' }),
      deals: count,
      value: (data.monthly_value[month] || 0) / 1000,
      settled: (data.monthly_settled_value[month] || 0) / 1000,
      commission: data.monthly_commission[month] || 0
    }));

  const sourceData = Object.entries(data.source_breakdown).map(([name, value]) => ({
    name: name.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase()),
    value
  }));

  const typeData = Object.entries(data.finance_type_counts).map(([name, count]) => ({
    name: name.charAt(0).toUpperCase() + name.slice(1),
    count,
    value: (data.finance_type_values[name] || 0) / 1000
  }));

  const funnelData = Object.entries(data.conversion_funnel)
    .filter(([stage]) => !['declined', 'lost'].includes(stage))
    .map(([stage, count]) => ({
      stage: STAGE_LABELS[stage] || stage,
      count
    }));

  const totalDeals = Object.values(data.conversion_funnel).reduce((s, v) => s + v, 0);
  const totalValue = Object.values(data.monthly_value).reduce((s, v) => s + v, 0);

  return (
    <CRMLayout>
      <div className="pb-20">
        {/* Header */}
        <div className="bg-navy-800 border-b border-white/5">
          <div className="px-6 py-6">
            <h1 className="text-3xl font-heading text-white uppercase tracking-wide" data-testid="analytics-title">
              Analytics & Reports
            </h1>
            <p className="text-gray-400 mt-1">Performance insights for your finance business</p>
          </div>
        </div>

        <div className="px-6 py-8 space-y-8">
          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <SummaryCard
              title="Total Deals"
              value={totalDeals}
              icon={<Users className="h-5 w-5" />}
              color="purple"
            />
            <SummaryCard
              title="Total Pipeline"
              value={`$${(totalValue / 1000).toFixed(0)}k`}
              icon={<DollarSign className="h-5 w-5" />}
              color="green"
            />
            <SummaryCard
              title="Est. Commission"
              value={`$${data.commission_summary.total_estimated.toLocaleString('en-AU')}`}
              icon={<TrendingUp className="h-5 w-5" />}
              color="blue"
            />
            <SummaryCard
              title="Commission Earned"
              value={`$${data.commission_summary.total_earned.toLocaleString('en-AU')}`}
              icon={<Target className="h-5 w-5" />}
              color="emerald"
            />
          </div>

          {/* Charts Row 1: Monthly Deals & Value */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <ChartCard title="Monthly Deal Volume" testId="chart-monthly-deals">
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={monthlyData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
                  <XAxis dataKey="month" stroke="#6b7280" fontSize={12} />
                  <YAxis stroke="#6b7280" fontSize={12} />
                  <Tooltip contentStyle={{ background: '#1e1b4b', border: '1px solid #4c1d95', borderRadius: 8 }} />
                  <Bar dataKey="deals" fill="#8b5cf6" radius={[4, 4, 0, 0]} name="Deals" />
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>

            <ChartCard title="Monthly Pipeline Value ($k)" testId="chart-monthly-value">
              <ResponsiveContainer width="100%" height={280}>
                <LineChart data={monthlyData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
                  <XAxis dataKey="month" stroke="#6b7280" fontSize={12} />
                  <YAxis stroke="#6b7280" fontSize={12} />
                  <Tooltip contentStyle={{ background: '#1e1b4b', border: '1px solid #4c1d95', borderRadius: 8 }} />
                  <Legend />
                  <Line type="monotone" dataKey="value" stroke="#8b5cf6" name="New ($k)" strokeWidth={2} dot={{ r: 4 }} />
                  <Line type="monotone" dataKey="settled" stroke="#10b981" name="Settled ($k)" strokeWidth={2} dot={{ r: 4 }} />
                </LineChart>
              </ResponsiveContainer>
            </ChartCard>
          </div>

          {/* Charts Row 2: Source & Type Breakdown */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <ChartCard title="Lead Sources" testId="chart-lead-sources">
              {sourceData.length > 0 ? (
                <ResponsiveContainer width="100%" height={250}>
                  <PieChart>
                    <Pie data={sourceData} cx="50%" cy="50%" outerRadius={80} dataKey="value" label={({ name, value }) => `${name} (${value})`}>
                      {sourceData.map((_, idx) => (
                        <Cell key={idx} fill={COLORS[idx % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip contentStyle={{ background: '#1e1b4b', border: '1px solid #4c1d95', borderRadius: 8 }} />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <EmptyState text="No source data yet" />
              )}
            </ChartCard>

            <ChartCard title="Finance Type Breakdown" testId="chart-finance-types">
              {typeData.length > 0 ? (
                <ResponsiveContainer width="100%" height={250}>
                  <BarChart data={typeData} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
                    <XAxis type="number" stroke="#6b7280" fontSize={12} />
                    <YAxis dataKey="name" type="category" stroke="#6b7280" fontSize={12} width={80} />
                    <Tooltip contentStyle={{ background: '#1e1b4b', border: '1px solid #4c1d95', borderRadius: 8 }} />
                    <Bar dataKey="count" fill="#3b82f6" radius={[0, 4, 4, 0]} name="Deals" />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <EmptyState text="No deals yet" />
              )}
            </ChartCard>

            <ChartCard title="Conversion Funnel" testId="chart-funnel">
              {funnelData.some(d => d.count > 0) ? (
                <div className="space-y-2 py-4">
                  {funnelData.map((item, idx) => {
                    const maxCount = Math.max(...funnelData.map(d => d.count), 1);
                    const width = Math.max((item.count / maxCount) * 100, 8);
                    return (
                      <div key={item.stage} className="flex items-center gap-3">
                        <span className="text-gray-400 text-xs w-20 text-right">{item.stage}</span>
                        <div className="flex-1 h-7 bg-navy-900 rounded overflow-hidden">
                          <div
                            className="h-full rounded flex items-center px-2 transition-all"
                            style={{ width: `${width}%`, backgroundColor: COLORS[idx % COLORS.length] }}
                          >
                            <span className="text-white text-xs font-bold">{item.count}</span>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <EmptyState text="No funnel data yet" />
              )}
            </ChartCard>
          </div>

          {/* Top Lenders */}
          {Object.keys(data.top_lenders).length > 0 && (
            <ChartCard title="Top Lenders" testId="chart-top-lenders">
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={Object.entries(data.top_lenders).map(([name, count]) => ({ name, count }))}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
                  <XAxis dataKey="name" stroke="#6b7280" fontSize={12} />
                  <YAxis stroke="#6b7280" fontSize={12} />
                  <Tooltip contentStyle={{ background: '#1e1b4b', border: '1px solid #4c1d95', borderRadius: 8 }} />
                  <Bar dataKey="count" fill="#10b981" radius={[4, 4, 0, 0]} name="Deals" />
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>
          )}
        </div>
      </div>
    </CRMLayout>
  );
}

function SummaryCard({ title, value, icon, color }) {
  const colors = {
    purple: 'from-purple-500/20 to-purple-600/5 border-purple-500/30 text-purple-400',
    green: 'from-green-500/20 to-green-600/5 border-green-500/30 text-green-400',
    blue: 'from-blue-500/20 to-blue-600/5 border-blue-500/30 text-blue-400',
    emerald: 'from-emerald-500/20 to-emerald-600/5 border-emerald-500/30 text-emerald-400'
  };

  return (
    <div className={`bg-gradient-to-br ${colors[color]} border rounded-xl p-5`}>
      <div className="flex items-center justify-between mb-1">
        <span className="text-gray-400 text-sm">{title}</span>
        {icon}
      </div>
      <p className="text-2xl font-heading text-white">{value}</p>
    </div>
  );
}

function ChartCard({ title, children, testId }) {
  return (
    <div className="bg-navy-800 rounded-xl border border-white/5 p-6" data-testid={testId}>
      <h3 className="text-white font-semibold mb-4">{title}</h3>
      {children}
    </div>
  );
}

function EmptyState({ text }) {
  return (
    <div className="flex items-center justify-center h-40 text-gray-500 text-sm">
      {text}
    </div>
  );
}
